import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import Pagination from '../../components/common/Pagination';
import { LuPlus, LuPencil, LuTrash2, LuSearch, LuBuilding2, LuEye, LuEyeOff } from 'react-icons/lu';

const EMPTY = { name: '', email: '', password: '', confirmPassword: '', phone: '', address: '', contactPerson: '' };

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const PAGE_SIZE = 10;
  const [modal, setModal] = useState({ open: false, mode: 'add', data: null });
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search) params.set('search', search);
      const { data } = await api.get(`/companies?${params}`);
      setCompanies(data.data || []);
      setPagination(data.pagination || null);
    } catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const openAdd  = () => { setForm(EMPTY); setModal({ open: true, mode: 'add', data: null }); };
  const openEdit = c => { setForm({ ...c, password: '', confirmPassword: '' }); setModal({ open: true, mode: 'edit', data: c }); };
  const close    = () => { setModal({ open: false, mode: 'add', data: null }); setShowPwd(false); setShowConfirmPwd(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (modal.mode === 'add' && form.password !== form.confirmPassword) {
      toast.error('Passwords do not match'); return;
    }
    if (modal.mode === 'edit' && form.password && form.password !== form.confirmPassword) {
      toast.error('Passwords do not match'); return;
    }
    setSaving(true);
    try {
      const { confirmPassword, ...payload } = form;
      if (modal.mode === 'edit' && !payload.password) delete payload.password;
      if (modal.mode === 'add') await api.post('/companies', payload);
      else {
        await api.put(`/companies/${modal.data._id}`, payload);
        if (form.password) await api.patch(`/companies/${modal.data._id}/password`, { password: form.password });
      }
      toast.success(modal.mode === 'add' ? 'Company added!' : 'Company updated!');
      close(); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this company?')) return;
    try { await api.delete(`/companies/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleStatus = async (c) => {
    try { await api.put(`/companies/${c._id}`, { status: c.status === 'active' ? 'inactive' : 'active' }); toast.success('Status updated'); load(); }
    catch { toast.error('Failed'); }
  };

  const prevSearch = useRef(search);
  useEffect(() => { if (prevSearch.current !== search) { prevSearch.current = search; setPage(1); } });

  const columns = [
    { key: 'name', label: 'Company', render: r => <span className="font-semibold text-hajj-dark">{r.name}</span> },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'contactPerson', label: 'Contact Person' },
    { key: 'status', label: 'Status', render: r => (
      <button onClick={() => toggleStatus(r)} className={`badge cursor-pointer transition-opacity hover:opacity-75 ${r.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
      </button>
    )},
    { key: 'actions', label: '', width: 80, render: r => (
      <div className="flex gap-1.5">
        <button onClick={() => openEdit(r)} className="w-8 h-8 rounded-lg bg-hajj-teal/10 text-teal-700 flex items-center justify-center hover:bg-hajj-teal/20 transition-colors"><LuPencil size={14} /></button>
        <button onClick={() => handleDelete(r._id)} className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors"><LuTrash2 size={14} /></button>
      </div>
    )},
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-hajj-dark">
          <LuBuilding2 className="text-xl sm:text-2xl" />
          <h1 className="text-xl sm:text-2xl font-extrabold">Hajj Companies</h1>
        </div>
        <button className="btn-primary text-sm" onClick={openAdd}><LuPlus size={15} /> Add Company</button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
        <div className="relative w-full sm:max-w-xs">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input placeholder="Search companies..." value={search} onChange={e => setSearch(e.target.value)} className="input-field pl-9 w-full" />
        </div>
        <span className="text-sm text-gray-400 sm:ml-auto">{pagination?.total ?? companies.length} companies</span>
      </div>

      <div className="card">
        <Table columns={columns} data={companies} loading={loading} emptyMessage="No companies found" />
        <Pagination page={page} total={pagination?.total || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>

      <Modal isOpen={modal.open} onClose={close} title={modal.mode === 'add' ? 'Add Company' : 'Edit Company'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Company Name *</label>
              <input value={form.name} onChange={set('name')} required className="input-field" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email *</label>
              <input type="email" value={form.email} onChange={set('email')} required className="input-field" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                {modal.mode === 'add' ? 'Password *' : 'New Password'}
              </label>
              <div className="relative">
                <input type={showPwd ? 'text' : 'password'} value={form.password} onChange={set('password')} required={modal.mode === 'add'} className="input-field pr-10" />
                <button type="button" onClick={() => setShowPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                  {showPwd ? <LuEyeOff size={15} /> : <LuEye size={15} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                {modal.mode === 'add' ? 'Confirm Password *' : 'Confirm New Password'}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPwd ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={set('confirmPassword')}
                  required={modal.mode === 'add'}
                  className={`input-field pr-10 ${form.confirmPassword && form.confirmPassword !== form.password ? 'border-red-300 focus:ring-red-200' : ''}`}
                />
                <button type="button" onClick={() => setShowConfirmPwd(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
                  {showConfirmPwd ? <LuEyeOff size={15} /> : <LuEye size={15} />}
                </button>
              </div>
              {form.confirmPassword && form.confirmPassword !== form.password && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone *</label>
              <div className="flex w-full border border-gray-200 rounded-xl overflow-hidden focus-within:border-hajj-teal focus-within:ring-2 focus-within:ring-hajj-teal/10 transition-all duration-200">
                <span className="flex items-center px-3 bg-gray-50 border-r border-gray-200 text-sm text-gray-600 font-medium select-none whitespace-nowrap">+966</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  value={form.phone.replace(/^\+966/, '')}
                  onChange={e => { const d = e.target.value.replace(/\D/g, ''); setForm(f => ({ ...f, phone: '+966' + d })); }}
                  required
                  maxLength={9}
                  placeholder="5XXXXXXXX"
                  className="flex-1 px-3 py-2.5 text-sm text-gray-800 bg-white outline-none"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Contact Person</label>
              <input value={form.contactPerson || ''} onChange={set('contactPerson')} className="input-field" />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Address</label>
              <input value={form.address || ''} onChange={set('address')} className="input-field" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
              {modal.mode === 'add' ? 'Add Company' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
